import type { SyntheticEvent } from "react";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { getFormInputProps } from "util/forms";
import { selectCurrencies } from "features/general/selectors";
import { downloadLocalFile } from "features/general/api";
import { useDownloadFile } from "hooks";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { DownloadFile } from "ui/forms/formik-elements";
import When from "ui/support/When";
import { ReviewableInput } from "ui/widgets/Input";
import OnboardingForm from "ui/forms/Onboarding/OnboardingForm";
import { FormField, type OnboardingBankingAndFinanceProps } from "./types";
import OnboardingReviewableFormInput from "../../OnboardingReviewableFormInput";

const BankingAndFinance = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingBankingAndFinanceProps) => {
    const { download } = useDownloadFile(downloadLocalFile);

    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h6'
                gutterBottom
            >
                Bank Account Details
            </Typography>
            <OnboardingReviewableFormInput
                name={FormField.AccountHolderName}
                values={values}
                renderLabel={() => 'Account Holder Name'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.AccountHolderName, form)}
                        fullWidth
                        required
                        size='small'
                        name={FormField.AccountHolderName}
                        value={values[FormField.AccountHolderName]}
                        label='Account Holder Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.BankName}
                values={values}
                renderLabel={() => 'Bank Name'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.BankName, form)}
                        fullWidth
                        required
                        size='small'
                        name={FormField.BankName}
                        value={values[FormField.BankName]}
                        label='Bank Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.Iban}
                values={values}
                renderLabel={() => 'IBAN / Account Number'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.Iban, form)}
                        fullWidth
                        required
                        size='small'
                        name={FormField.Iban}
                        value={values[FormField.Iban]}
                        label='IBAN / Account Number'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.Swift}
                values={values}
                renderLabel={() => 'SWIFT Code'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.Swift, form)}
                        fullWidth
                        size='small'
                        name={FormField.Swift}
                        value={values[FormField.Swift]}
                        label='SWIFT Code'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
            >
                Proof of Bank Account Ownership
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Recent bank statement (dated within the last 3 months) or bank account ownership letter.
            </Typography>
            <OnboardingReviewableFormInput
                name={FormField.ProofFileKey}
                values={values}
                renderLabel={() => 'Proof of Bank Account Ownership'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                slots={{
                    actions: (
                        <>
                            <ReviewableInput.ApproveAction />
                            <ReviewableInput.RejectAction />
                            <ReviewableInput.CommentAction />
                        </>
                    )
                }}
                renderFormInput={({ reviewableStatus }) => (
                    <DownloadFile
                        downloadFile={() => download({
                            fileKey: String(values[FormField.ProofFileKey])
                        })}
                        file={String(values[FormField.ProofFileKey])}
                    >
                        {reviewableStatus}
                    </DownloadFile>
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                What is your preferred currency?
            </Typography>
            <OnboardingReviewableFormInput
                name={FormField.Currency}
                values={values}
                renderLabel={() => 'Preferred Currency'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                    <StatefulSelector
                        fullWidth
                        disableClearable
                        disabled={disabled}
                        layout={null}
                        selectOptions={selectCurrencies}
                        value={values[FormField.Currency]}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Currency, form),
                            inputRef,
                            onBlur: handleBlur,
                            required: true,
                            fullWidth: true,
                            name: FormField.Currency,
                            label: 'Preferred Currency',
                            InputProps: {
                                endAdornment: reviewableStatus
                            }
                        }}
                        onChange={onAutocompleteChange(FormField.Currency)}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Financial Information
            </Typography>
            <OnboardingReviewableFormInput
                name={FormField.AvgTransactionSize}
                values={values}
                renderLabel={() => 'Average Transaction Size'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.AvgTransactionSize, form)}
                        fullWidth
                        required
                        type='number'
                        size='small'
                        name={FormField.AvgTransactionSize}
                        value={values[FormField.AvgTransactionSize]}
                        label='Average Transaction Size'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.MonthlyIncome}
                values={values}
                renderLabel={() => 'Estimated Monthly Transaction Amount'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.MonthlyIncome, form)}
                        fullWidth
                        required
                        type='number'
                        size='small'
                        name={FormField.MonthlyIncome}
                        value={values[FormField.MonthlyIncome]}
                        label='Estimated Monthly Transaction Amount'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}
                                >
                                    <When
                                        if={values[FormField.Currency]}
                                        then={(
                                            <Typography
                                                variant='caption'
                                                color='textSecondary'
                                            >
                                                {values[FormField.Currency]}
                                            </Typography>
                                        )}
                                    />
                                    {reviewableStatus}
                                </Box>
                            )
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.TransactionVolume}
                values={values}
                renderLabel={() => 'Expected Transaction Volume'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.TransactionVolume, form)}
                        fullWidth
                        required
                        type='number'
                        size='small'
                        name={FormField.TransactionVolume}
                        value={values[FormField.TransactionVolume]}
                        label='Expected Transaction Volume'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
        </OnboardingForm>
    );
};

export default BankingAndFinance;
